/**
 * TanStack Query cancellation and browser/fetch abort signals.
 * oRPC often wraps AbortError as "Cannot parse response body" with the abort
 * in `error.cause` — walk the chain so callers don't treat cancels as app bugs.
 */

function errorName(err: unknown): string | undefined {
	if (!err || typeof err !== "object") {
		return undefined;
	}
	const name = (err as { name?: unknown }).name;
	return typeof name === "string" ? name : undefined;
}

function errorMessage(err: unknown): string | undefined {
	if (typeof err === "string") {
		return err;
	}
	if (!err || typeof err !== "object") {
		return undefined;
	}
	const message = (err as { message?: unknown }).message;
	return typeof message === "string" ? message : undefined;
}

function isAbortNameOrMessage(err: unknown): boolean {
	const name = errorName(err);
	if (name === "AbortError" || name === "TimeoutError") {
		return true;
	}
	const message = errorMessage(err);
	if (!message) {
		return false;
	}
	const lower = message.toLowerCase();
	return (
		lower === "aborterror" ||
		lower.includes("fetch is aborted") ||
		lower.includes("the operation was aborted") ||
		lower.includes("signal is aborted") ||
		lower.includes("aborted without reason") ||
		// Some runtimes use a bare DOMException message.
		lower === "aborted"
	);
}

export function isCancelledError(err: unknown): boolean {
	if (typeof err === "string") {
		return err === "CancelledError" || err.includes("CancelledError");
	}
	if (err instanceof Error) {
		// In practice we may see:
		// - err.name === "CancelledError"
		// - err.name === "Error" && err.message === "CancelledError"
		return (
			err.name === "CancelledError" ||
			err.message === "CancelledError" ||
			err.message.includes("CancelledError")
		);
	}
	if (!err || typeof err !== "object") {
		return false;
	}
	return (
		(err as { name?: unknown; message?: unknown }).name === "CancelledError"
	);
}

/** True when this error or any `cause` is a fetch/request abort. */
export function isAbortError(err: unknown, maxDepth = 6): boolean {
	let current: unknown = err;
	for (let depth = 0; depth < maxDepth; depth += 1) {
		if (current == null) {
			return false;
		}
		if (isAbortNameOrMessage(current)) {
			return true;
		}
		if (typeof current !== "object" || !("cause" in current)) {
			return false;
		}
		current = (current as { cause?: unknown }).cause;
	}
	return false;
}

/** Request cancelled by TanStack Query or aborted by AbortSignal / navigation. */
export function isAbortOrCancelledError(err: unknown): boolean {
	return isCancelledError(err) || isAbortError(err);
}

/**
 * Await a promise but treat TanStack cancellation as non-fatal.
 * - Returns `null` on CancelledError
 * - Re-throws non-cancellation errors (so behavior doesn't silently change)
 */
export async function awaitOrIgnoreCancelled<T>(
	promise: Promise<T>,
): Promise<T | null> {
	try {
		return await promise;
	} catch (err) {
		if (isCancelledError(err)) {
			return null;
		}
		throw err;
	}
}

/**
 * Fire-and-forget but swallow TanStack cancellation rejections.
 * Logs non-cancellation errors to console by default.
 */
export function fireAndForgetOrIgnoreCancelled(
	promise: Promise<unknown>,
	label: string,
): void {
	void promise.catch((err) => {
		if (isCancelledError(err)) {
			return;
		}
		console.error(label, err);
	});
}
